"use client";
import { useMemo } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { Scrollbar } from "@onair/minimal-ui/scrollbar";
import { OnlineCourseStatus } from "@onair/repositories";
import OnlineCourseStatusLabel from "@onair/ui/label/online-course-status-label";

interface GroupedCourseData {
	course: {
		id: string;
		name: string;
		status: string;
	};
	lessons: {
		title: string;
		resources?: {
			id: string;
			name?: string;
			fileName?: string;
		}[];
	}[];
}

interface ResourceDeleteDialogProps {
	open: boolean;
	onClose: () => void;
	onConfirm: () => void;
	fileName: string;
	courseData: any[] | null;
	isLoading?: boolean;
}

const ResourceDeleteDialog = ({
	open,
	onClose,
	onConfirm,
	fileName,
	courseData,
	isLoading = false,
}: ResourceDeleteDialogProps) => {
	// Process course data for both resource and library deletions
	const groupedCourseData = useMemo(() => {
		if (!courseData?.length) return [];

		// Check if courseData is in the library format (with sections)
		const isLibraryData = courseData.some((course) => course.sections);

		if (isLibraryData) {
			// Library format
			return courseData.map((course) => {
				// Collect all lessons from all sections with resources
				const lessons =
					course.sections?.flatMap(
						(section) =>
							section.lessons?.map((lesson) => ({
								title: lesson.title,
								resources: lesson.resources || [],
							})) || [],
					) || [];

				return {
					course: {
						id: course.id,
						name: course.name,
						status: course.status,
					},
					lessons,
				};
			});
		} else {
			// Resource format (existing logic)
			const groupedData: Record<string, GroupedCourseData> = {};

			courseData.forEach((item) => {
				const courseId = item.course.id;

				if (!groupedData[courseId]) {
					groupedData[courseId] = {
						course: item.course,
						lessons: [],
					};
				}

				// Add lesson if not already included
				const lessonExists = groupedData[courseId].lessons.some(
					(lesson) => lesson.title === item.lesson.title,
				);

				if (!lessonExists) {
					groupedData[courseId].lessons.push({
						title: item.lesson.title,
						resources: item.resources || [],
					});
				}
			});

			return Object.values(groupedData);
		}
	}, [courseData]);

	// Check if the resource/library is used in published or pending approval courses
	const hasPublishedOrPendingCourses = useMemo(() => {
		if (!groupedCourseData?.length) return false;
		return groupedCourseData.some(
			(item) =>
				item.course.status === OnlineCourseStatus.PUBLISHED ||
				item.course.status === OnlineCourseStatus.PENDING_APPROVAL_WITH_ADMIN,
		);
	}, [groupedCourseData]);

	// Determine the type of item being deleted (resource or library)
	const itemType = useMemo(() => {
		// If courseData has the sections structure, it's a library
		if (courseData?.length && courseData[0]?.sections) {
			return "thư mục";
		}
		return "tài nguyên";
	}, [courseData]);

	// Show loading state
	if (isLoading) {
		return (
			<Dialog
				open={open}
				onClose={onClose}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle id="alert-dialog-title">
					{`Đang kiểm tra ${itemType}`}
				</DialogTitle>
				<DialogContent>
					<Box className="flex flex-col items-center justify-center p-8">
						<CircularProgress size={40} />
						<Typography variant="body1" className="mt-4">
							Đang kiểm tra xem {itemType} có đang được sử dụng không...
						</Typography>
					</Box>
				</DialogContent>
			</Dialog>
		);
	}

	if (hasPublishedOrPendingCourses) {
		return (
			<Dialog
				open={open}
				onClose={onClose}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle id="alert-dialog-title">
					{`${itemType.charAt(0).toUpperCase() + itemType.slice(1)} đang được sử dụng`}
				</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						<Box className="mb-4 text-black">
							{`${itemType.charAt(0).toUpperCase() + itemType.slice(1)} "${fileName}" đang được sử dụng trong các khóa học:`}
						</Box>
						<Scrollbar fillContent>
							<Box
								sx={{
									maxHeight: "250px",
									overflowY: "auto",
									border: "1px solid",
									borderColor: "divider",
									borderRadius: 1,
									p: 1,
								}}
							>
								{groupedCourseData
									.filter(
										(item) =>
											item.course.status === OnlineCourseStatus.PUBLISHED ||
											item.course.status ===
											OnlineCourseStatus.PENDING_APPROVAL_WITH_ADMIN,
									)
									.map((item, index, filteredArray) => (
										<Box
											key={item.course.id}
											sx={{
												mb: 1,
												p: 1,
												borderBottom:
													index < filteredArray.length - 1
														? "1px solid"
														: "none",
												borderColor: "divider",
											}}
										>
											<Stack
												direction="row"
												spacing={1}
												className="justify-between mb-2 items-center"
											>
												<Typography
													variant="subtitle1"
													className="overflow-hidden text-ellipsis"
												>
													{item.course.name}
												</Typography>
												<Box className="min-w-fit">
													<OnlineCourseStatusLabel
														status={item.course.status as OnlineCourseStatus}
													/>
												</Box>
											</Stack>
											<Typography variant="body2" sx={{ mt: 0.5 }}>
												Bài giảng:
											</Typography>
											<Box component="ul" ml={2} mt={0.5}>
												{item.lessons.map((lesson, lessonIndex) => (
													<Box
														component="li"
														key={lessonIndex}
														sx={{ opacity: 0.75 }}
													>
														<Typography variant="body2">
															- {lesson.title}
															{lesson.resources &&
																lesson.resources.length > 0 && (
																	<Box component="ul" ml={2} mt={0.5}>
																		{lesson.resources.map(
																			(resource, resourceIndex) => (
																				<Box
																					component="li"
																					key={`${lessonIndex}-${resourceIndex}`}
																					sx={{ opacity: 0.9 }}
																				>
																					<Typography
																						variant="body2"
																						fontStyle="italic"
																					>
																						•{" "}
																						{resource.fileName || resource.name}
																					</Typography>
																				</Box>
																			),
																		)}
																	</Box>
																)}
														</Typography>
													</Box>
												))}
											</Box>
										</Box>
									))}
							</Box>
						</Scrollbar>
					</DialogContentText>
				</DialogContent>
				<DialogActions className="flex justify-between">
					<Box>Bạn không thể xóa {itemType} này.</Box>
					<Button onClick={onClose} variant="outlined">
						Đóng
					</Button>
				</DialogActions>
			</Dialog>
		);
	}

	return (
		<Dialog
			open={open}
			onClose={onClose}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
			maxWidth="sm"
			fullWidth
		>
			<DialogTitle id="alert-dialog-title">
				{groupedCourseData.length > 0
					? `${itemType.charAt(0).toUpperCase() + itemType.slice(1)} đang được sử dụng`
					: `Xoá ${itemType}`}
			</DialogTitle>
			<DialogContent>
				<DialogContentText id="alert-dialog-description">
					{groupedCourseData.length > 0 ? (
						<>
							<Box className="mb-4 text-black">
								{`${itemType.charAt(0).toUpperCase() + itemType.slice(1)} "${fileName}" đang được sử dụng trong các khóa học:`}
							</Box>
							<Scrollbar fillContent>
								<Box
									sx={{
										maxHeight: "200px",
										overflowY: "auto",
										border: "1px solid",
										borderColor: "divider",
										borderRadius: 1,
										p: 1,
									}}
								>
									{groupedCourseData.map((item, index) => (
										<Box
											key={item.course.id}
											sx={{
												mb: 1,
												p: 1,
												borderBottom:
													index < groupedCourseData.length - 1
														? "1px solid"
														: "none",
												borderColor: "divider",
											}}
										>
											<Stack
												direction="row"
												spacing={1}
												className="justify-between mb-2 items-center"
											>
												<Typography
													variant="subtitle1"
													className="overflow-hidden text-ellipsis"
												>
													{item.course.name}
												</Typography>
												<Box className="min-w-fit">
													<OnlineCourseStatusLabel
														status={item.course.status as OnlineCourseStatus}
													/>
												</Box>
											</Stack>
											<Typography variant="body2" sx={{ mt: 0.5 }}>
												Bài giảng:
											</Typography>
											<Box component="ul" ml={2} mt={0.5}>
												{item.lessons.map((lesson, lessonIndex) => (
													<Box
														component="li"
														key={lessonIndex}
														sx={{ opacity: 0.75 }}
													>
														<Typography variant="body2">
															- {lesson.title}
															{lesson.resources &&
																lesson.resources.length > 0 && (
																	<Box component="ul" ml={2} mt={0.5}>
																		{lesson.resources.map(
																			(resource, resourceIndex) => (
																				<Box
																					component="li"
																					key={`${lessonIndex}-${resourceIndex}`}
																					sx={{ opacity: 0.9 }}
																				>
																					<Typography
																						variant="body2"
																						fontStyle="italic"
																					>
																						•{" "}
																						{resource.fileName || resource.name}
																					</Typography>
																				</Box>
																			),
																		)}
																	</Box>
																)}
														</Typography>
													</Box>
												))}
											</Box>
										</Box>
									))}
								</Box>
							</Scrollbar>
						</>
					) : (
						<Box className="mb-4 text-black">
							{`${itemType.charAt(0).toUpperCase() + itemType.slice(1)} "${fileName}" sẽ bị xoá.`}
						</Box>
					)}
					<Box mt={2}>Bạn có chắc chắn muốn tiếp tục?</Box>
				</DialogContentText>
			</DialogContent>

			<DialogActions>
				<Button variant="outlined" onClick={onClose} autoFocus>
					Huỷ
				</Button>
				<Button variant="contained" color="error" onClick={onConfirm}>
					Xoá
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ResourceDeleteDialog;
